import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';

import {
  KnowledgeDimension,
  BloomCategory,
  WorksheetAiModel,
  WorksheetAiConfig,
} from 'types/Worksheets';
import { logBrazeEvent } from 'utils/brazeEvents';
import {
  useCreateAiWorksheetFromArticle,
  useGetAiWorksheetJobStatus,
  Worksheet,
} from './useWorksheets';

type UseAiWorksheetGenerationResult = {
  generateWorksheet: (articleId: string, config: WorksheetAiConfig) => void;
  worksheet: Worksheet | null;
  isLoading: boolean;
  isPolling: boolean;
  progress: number;
  progressMessage: string;
  error: string | null;
  reset: () => void;
};

export const useAiWorksheetGeneration = (
  articleIdForResume?: string,
  options?: { active?: boolean }
): UseAiWorksheetGenerationResult => {
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const isActive = options?.active ?? true;

  const storageKey = useMemo(
    () => (articleIdForResume ? `ai-worksheet-job:${articleIdForResume}` : null),
    [articleIdForResume]
  );

  const createMutation = useCreateAiWorksheetFromArticle();
  const statusMutation = useGetAiWorksheetJobStatus();

  // Tracks which worksheet id we've already fired 'worksheet_created' for, so
  // re-renders after completion (both the synchronous and polling paths below
  // funnel through this state) don't duplicate the event.
  const trackedWorksheetIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (worksheet && trackedWorksheetIdRef.current !== worksheet.id) {
      trackedWorksheetIdRef.current = worksheet.id;
      logBrazeEvent('worksheet_created', { ai_generated: true });
    }
  }, [worksheet]);

  const reset = useCallback(() => {
    setWorksheet(null);
    setIsPolling(false);
    setProgress(0);
    setProgressMessage('');
    setError(null);
    setCurrentJobId(null);
    if (storageKey) {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'localStorage',
          message: 'Failed to remove localStorage item',
          level: 'error',
          data: {
            error,
          },
        });
        Sentry.captureException(error);
      }
    }
  }, [storageKey]);

  const pollJobStatus = useCallback(
    async (jobId: string) => {
      // Avoid overlapping requests if a previous poll is still pending
      if (statusMutation.isPending) return;
      try {
        const result = await statusMutation.mutateAsync(jobId);

        if ('id' in result) {
          // This is a Worksheet object - job completed
          setWorksheet(result);
          setIsPolling(false);
          setProgress(100);
          setProgressMessage('Worksheet generated successfully!');
          setCurrentJobId(null);
          if (storageKey) {
            try {
              window.localStorage.removeItem(storageKey);
            } catch {
              /* ignore localStorage errors */
            }
          }
        } else {
          // This is a job status response
          setProgress(result.progress);
          setProgressMessage(result.progressMessage);

          if (result.error) {
            Sentry.captureException(result.error);
            setError(result.error);
            setIsPolling(false);
            setCurrentJobId(null);
            if (storageKey) {
              try {
                window.localStorage.removeItem(storageKey);
              } catch (error) {
                Sentry.addBreadcrumb({
                  category: 'localStorage',
                  message: 'Failed to remove localStorage item',
                  level: 'error',
                  data: {
                    error,
                  },
                });
                Sentry.captureException(error);
              }
            }
          } else if (result.status === 'FAILED') {
            Sentry.captureException(result.error);
            setError('Worksheet generation failed');
            setIsPolling(false);
            setCurrentJobId(null);
            if (storageKey) {
              try {
                window.localStorage.removeItem(storageKey);
              } catch (error) {
                Sentry.addBreadcrumb({
                  category: 'localStorage',
                  message: 'Failed to remove localStorage item',
                  level: 'error',
                  data: {
                    error,
                  },
                });
                Sentry.captureException(error);
              }
            }
          } else if (result.status === 'COMPLETED') {
            // Job completed but no worksheet returned - this shouldn't happen
            setError('Worksheet generation completed but no worksheet was returned');
            setIsPolling(false);
            setCurrentJobId(null);
            if (storageKey) {
              try {
                window.localStorage.removeItem(storageKey);
              } catch (error) {
                Sentry.addBreadcrumb({
                  category: 'localStorage',
                  message: 'Failed to remove localStorage item',
                  level: 'error',
                  data: {
                    error,
                  },
                });
                Sentry.captureException(error);
              }
            }
          }
          // If status is PENDING or PROCESSING, continue polling
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to check job status');
        setIsPolling(false);
        setCurrentJobId(null);
        Sentry.captureException(err);
        if (storageKey) {
          try {
            window.localStorage.removeItem(storageKey);
          } catch (error) {
            Sentry.addBreadcrumb({
              category: 'localStorage',
              message: 'Failed to remove localStorage item',
              level: 'error',
              data: {
                error,
              },
            });
            Sentry.captureException(error);
          }
        }
      }
    },
    [statusMutation, storageKey]
  );

  // Polling effect
  useEffect(() => {
    if (!isPolling || !currentJobId || !isActive) return;

    const interval = setInterval(() => {
      pollJobStatus(currentJobId);
    }, 4000); // Poll every 4 seconds

    return () => {
      clearInterval(interval);
    };
  }, [isPolling, currentJobId, pollJobStatus, isActive]);

  // Auto-resume a running job for this article (same-browser) if found in localStorage
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed: { jobId: string; startedAt?: number } = JSON.parse(raw);
        if (parsed?.jobId) {
          setCurrentJobId(parsed.jobId);
          setIsPolling(true);
          setProgress((prev) => (prev > 0 ? prev : 5));
          // Only set fallback message if no progress message exists
          setProgressMessage((prev) => prev || 'Fortschritt wird geladen...');
          // Kick off an immediate status check on resume
        }
        // Do NOT remove the storage key here; keep it until completion/failure/reset
      }
    } catch (error) {
      Sentry.addBreadcrumb({
        category: 'localStorage',
        message: 'Failed to get localStorage item',
        level: 'error',
        data: {
          error,
        },
      });
      Sentry.captureException(error);
    }
    // Only run on mount/articleId change
  }, [storageKey, pollJobStatus]);

  const generateWorksheet = useCallback(
    async (articleId: string, config: WorksheetAiConfig) => {
      reset();
      try {
        const result = await createMutation.mutateAsync({
          articleId,
          config,
        });

        if ('id' in result) {
          // This is a Worksheet object - already completed
          setWorksheet(result);
          setProgress(100);
          setProgressMessage('Worksheet ready!');
        } else {
          // This is a job response - start polling
          setCurrentJobId(result.jobId);
          setProgress(result.progress);
          setProgressMessage(result.progressMessage);
          setIsPolling(true);
          if (storageKey) {
            try {
              window.localStorage.setItem(
                storageKey,
                JSON.stringify({ jobId: result.jobId, startedAt: Date.now() })
              );
            } catch (error) {
              Sentry.addBreadcrumb({
                category: 'localStorage',
                message: 'Failed to set localStorage item',
                level: 'error',
                data: {
                  error,
                },
              });
              Sentry.captureException(error);
            }
          }

          if (result.status === 'FAILED') {
            if (result.error?.includes('529') || result.error?.includes('overloaded_error')) {
              setError(
                'KI-Dienst ist vorübergehend überlastet. Bitte versuche es in 10–15 Minuten erneut.'
              );
              setIsPolling(false);
              if (storageKey) {
                try {
                  window.localStorage.removeItem(storageKey);
                } catch (error) {
                  Sentry.addBreadcrumb({
                    category: 'localStorage',
                    message: 'Failed to remove localStorage item',
                    level: 'error',
                    data: {
                      error,
                    },
                  });
                  Sentry.captureException(error);
                }
              }

              return;
            }
            setError(result.error || 'Worksheet generation failed');
            setIsPolling(false);
            if (storageKey) {
              try {
                window.localStorage.removeItem(storageKey);
              } catch (error) {
                Sentry.addBreadcrumb({
                  category: 'localStorage',
                  message: 'Failed to remove localStorage item',
                  level: 'error',
                  data: {
                    error,
                  },
                });
                Sentry.captureException(error);
              }
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start worksheet generation');
        Sentry.captureException(err);
      }
    },
    [createMutation, reset, storageKey]
  );

  return {
    generateWorksheet,
    worksheet,
    isLoading: createMutation.isPending || statusMutation.isPending,
    isPolling,
    progress,
    progressMessage,
    error,
    reset,
  };
};
